/**
 * Shared PDFTron citation search / highlight helpers for FileViewerModal and AiWardenPdfPanel.
 */

export function normalizeCitationSearchPhrase(raw) {
    if (!raw || typeof raw !== 'string') return '';
    return raw.replace(/\s+/g, ' ').trim();
}

function buildSearchCandidates(raw) {
    const singleLine = normalizeCitationSearchPhrase(raw);
    if (!singleLine) return [];
    const out = [];
    const push = s => {
        const t = typeof s === 'string' ? s.trim() : '';
        if (t.length >= 4 && !out.includes(t)) out.push(t);
    };
    push(singleLine.slice(0, 320));
    push(singleLine.slice(0, 160));
    push(singleLine.slice(0, 80));
    const firstChunk = singleLine.split(/[.!?\n]/)[0];
    if (firstChunk && firstChunk.length >= 4) push(firstChunk.slice(0, 120));

    // Fallbacks for OCR/line-break differences: sentence and word-window probes.
    const sentences = singleLine
        .split(/[.!?;:]\s+/)
        .map(s => s.trim())
        .filter(Boolean);
    for (const s of sentences.slice(0, 4)) {
        push(s.slice(0, 120));
        push(s.slice(0, 80));
    }

    const words = singleLine.split(/\s+/).filter(Boolean);
    const addWindow = size => {
        if (words.length < size) return;
        for (let i = 0; i <= words.length - size; i += Math.max(1, Math.floor(size / 2))) {
            push(words.slice(i, i + size).join(' '));
            if (out.length > 24) break;
        }
    };
    addWindow(12);
    addWindow(8);
    return out;
}

async function highlightWithSearchStream(documentViewer, phrase) {
    if (typeof documentViewer.search !== 'function') return false;
    const stream = documentViewer.search(phrase);
    // eslint-disable-next-line no-restricted-syntax
    for await (const res of stream) {
        documentViewer.displaySearchResult(res);
        if (typeof documentViewer.setActiveSearchResult === 'function') {
            documentViewer.setActiveSearchResult(res);
        }
        return true;
    }
    return false;
}

function highlightWithTextSearchInit(instance, phrase) {
    const { documentViewer, Search } = instance.Core;
    if (!Search?.Mode || typeof documentViewer.textSearchInit !== 'function') return false;
    const mode = Search.Mode.HIGHLIGHT | Search.Mode.PAGE_STOP;
    let used = false;
    documentViewer.textSearchInit(phrase, mode, {
        fullSearch: true,
        onResult: result => {
            if (result.resultCode === Search.ResultCode.FOUND && !used) {
                used = true;
                documentViewer.displaySearchResult(result);
                if (typeof documentViewer.setActiveSearchResult === 'function') {
                    documentViewer.setActiveSearchResult(result);
                }
            }
        },
    });
    return true;
}

/**
 * @param {import('@pdftron/webviewer').WebViewerInstance} instance
 * @param {string} rawText - chunk / citation text to locate in the PDF
 * @param {{ pageOneBased?: number | null }} [options]
 */
export async function runCitationHighlightAsync(instance, rawText, options = {}) {
    const { documentViewer } = instance.Core;
    if (!documentViewer) return;

    const { pageOneBased } = options;
    if (pageOneBased != null && Number.isFinite(pageOneBased) && pageOneBased > 0) {
        try {
            if (typeof documentViewer.setCurrentPage === 'function') {
                documentViewer.setCurrentPage(pageOneBased);
            }
        } catch {
            /* ignore invalid page */
        }
    }

    try {
        documentViewer.clearSearchResults?.();
    } catch {
        /* ignore */
    }

    const candidates = buildSearchCandidates(rawText);
    if (!candidates.length) return;

    for (const p of candidates) {
        if (!p || p.length < 4) continue;
        try {
            if (await highlightWithSearchStream(documentViewer, p)) return;
        } catch {
            /* try next */
        }
    }
    for (const p of candidates) {
        if (!p || p.length < 4) continue;
        try {
            if (highlightWithTextSearchInit(instance, p)) return;
        } catch {
            /* try next */
        }
    }
}

/** @deprecated prefer runCitationHighlightAsync */
export function runCitationHighlight(instance, rawText) {
    const phrase = normalizeCitationSearchPhrase(rawText);
    if (!phrase || phrase.length < 4) return;
    void runCitationHighlightAsync(instance, rawText, {});
}
